<template>
  <RemarkCard
    :remark="remark"
    part-of="past_meetings"
    show-period
    hide-read-status
    show-tags
    disable-tag-links
    :exclude-tag-ids-in-card="excludedTagIdsInCard"
  >
    <template v-if="replyByTalkingPointId[remark.id]" #after-comments>
      <SfComment
        v-if="!editingTalkingPointProposal[remark.id]"
        :id="remark.id"
        :attachments="[]"
        :attachment-ids="[]"
        :author-name="authorName"
        :commentable-id="remark.id"
        commentable-type="Remark"
        :created-at="currentDate"
        :edited-at="null"
        :read="true"
        :rendered-text="htmlRepliesByTalkingPointId[remark.id]"
        :text="replyByTalkingPointId[remark.id]"
        :company-id="remark.companyId"
        :ledger-id="remark.ledgerId"
        class="
          tw-rounded-lg tw-border-sf-ai-light-2 tw-border-2 tw-border-solid
        "
      >
        <template #actions>
          <RemarkActions
            :allowed-actions="{
              updateTags: false,
              edit: true,
              openInContext: false,
              remove: true,
              toggleRead: false,
            }"
            :read="true"
            @click="(e) => handleReplyAction(e, remark.id)"
          />
        </template>
      </SfComment>
      <div
        v-else
        class="
          tw-grow
          tw-min-w-0
          tw-flex
          tw-flex-col
          tw-gap-2
          tw-px-6
          tw-py-2
          tw-relative
        "
      >
        <div class="tw-flex tw-gap-2 tw-items-center">
          <span
            class="
              tw-font-medium
              tw-text-sm
              tw-whitespace-nowrap
              tw-text-ellipsis
              tw-overflow-hidden
            "
          >
            {{ authorName }}
          </span>
          <time
            v-tooltip.timestamp="currentDate"
            class="tw-text-xs tw-text-sf-gray-light tw-whitespace-nowrap"
          >
            {{ currentDateFormatted }}
          </time>
        </div>
        <CommentForm
          commentable-type="Remark"
          :commentable-id="remark.id"
          :btn-text="I18n.t('common.reply')"
          :text="replyByTalkingPointId[remark.id]"
          :company-id="remark.companyId"
          :ledger-id="remark.ledgerId"
          @cancel="handleCancel"
          @submit="handleSubmit"
        />
      </div>
    </template>
    <template #custom-actions>
      <SfButtonDs
        v-if="!disableInteraction"
        size="sm"
        variant="secondary"
        class="
          tw-opacity-0
          group-hover:tw-opacity-100
          focus-within:tw-opacity-100
        "
        icon-before="arrow-left"
        @click.stop="openMoveBackConfirmationModal(remark)"
      >
        {{ I18n.t('client_meetings.past.remarks.move_back_to_open') }}
      </SfButtonDs>
    </template>
  </RemarkCard>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { storeToRefs } from 'pinia'

import { isCurrentWorkflowLocked } from '@app/adapters/silverfin'
import { SfButtonDs } from '@silverfin/ui-components'
import useI18n from '@app/shared/vue/composables/use-I18n'
import RemarkCard from '@app/shared/vue/components/molecules/remark/remark-card.vue'
import RemarkActions from '@app/shared/vue/components/molecules/remark/remark-actions.vue'
import SfComment from '@app/shared/vue/components/molecules/remark/sf-comment.vue'
import CommentForm from '@app/application/communication/sections/communication/components/comments/comment-form.vue'
import communicationDate from '@app/utils/communication-date'
import type { Remark } from '@app/application/communication/types'
import { getCurrentUserName } from '@app/adapters/silverfin'
import { useProposalDetailsStore } from '@app/application/client_meetings/screens/past/stores/proposal'
import { useTalkingPointTag } from '@app/application/client_meetings/composables/use-talking-point-tag'

interface Props {
  remark: Remark
  proposalId: number
}

const props = defineProps<Props>()

const I18n = useI18n()
const proposalStore = useProposalDetailsStore()
const {
  repliesByProposalIdTalkingPointId,
  htmlRepliesByProposalIdTalkingPointId,
} = storeToRefs(proposalStore)

const { talkingPointTagIds: excludedTagIdsInCard } = useTalkingPointTag()

const disableInteraction = isCurrentWorkflowLocked()

const replyByTalkingPointId = computed(
  () => repliesByProposalIdTalkingPointId.value?.[props.proposalId!] || {}
)

const htmlRepliesByTalkingPointId = computed(
  () => htmlRepliesByProposalIdTalkingPointId.value?.[props.proposalId!] || {}
)

const currentDate = new Date().toISOString()
const currentDateFormatted = communicationDate(currentDate)
const authorName = getCurrentUserName() || ''

const editingTalkingPointProposal = ref<Record<string, boolean>>({})

function setEditingTalkingPointProposal(
  talkingPointId: number,
  editing: boolean
): void {
  editingTalkingPointProposal.value = {
    ...editingTalkingPointProposal.value,
    [talkingPointId]: editing,
  }
}

function handleCancel({ commentableId }: { commentableId: number }): void {
  setEditingTalkingPointProposal(commentableId, false)
}

async function handleSubmit({
  commentableId,
}: {
  commentableId: number
}): Promise<void> {
  await proposalStore.deleteReply(props.proposalId!, commentableId)
  setEditingTalkingPointProposal(commentableId, false)
}

function handleReplyAction(action: string, talkingPointId: number): void {
  if (action === 'edit') {
    setEditingTalkingPointProposal(talkingPointId, true)
  } else if (action === 'remove') {
    window.sfConfirm({
      onConfirm: async () =>
        await proposalStore.deleteReply(props.proposalId!, talkingPointId),
    })
  }
}

function openMoveBackConfirmationModal(remark: Remark) {
  window.modalCitizen.openModalPortal('move-back-to-open-confirmation-modal', {
    remark,
  })
}
</script>
